export const getPercentCompletedGoal = (goal: any) => {
  if (goal && goal?.keyResults && goal?.keyResults.length > 0) {
    return computeAverageOfSummedupCompletionPercentOfKeyResults(
      goal?.keyResults,
    );
  }

  return computeCompletionMBO(
    goal?.startValue,
    goal?.targetValue,
    goal?.currentValue,
  );
};

export const getPercentOfKeyResultsGoal = (goal: any) => {
  if (goal && goal?.keyResults && goal?.keyResults.length > 0) {
    return computeAverageOfSummedupPercentOfKeyResults(goal?.keyResults);
  }

  return computeCompletionMBO(
    goal?.startValue,
    goal?.targetValue,
    goal?.currentValue,
  );
};

export type MetricDirection = 'increase' | 'decrease' | 'unchanged';

export const metricDirection = (
  startValue: number,
  targetValue: number,
): MetricDirection => {
  if (targetValue > startValue) return 'increase';
  if (targetValue < startValue) return 'decrease';
  return 'unchanged';
};

export const isMetricValueWithinStartBoundary = (
  startValue: number | null | undefined,
  currentValue: number | null | undefined,
  targetValue: number | null | undefined,
) => {
  const start = Number(startValue);
  const current = Number(currentValue);
  const target = Number(targetValue);

  if (
    !Number.isFinite(start) ||
    !Number.isFinite(current) ||
    !Number.isFinite(target)
  ) {
    return false;
  }

  const direction = metricDirection(start, target);
  if (direction === 'increase') return current >= start;
  if (direction === 'decrease') return current <= start;
  return current === start;
};

/**
 * Calculates progress as the distance travelled from the starting value to the
 * target. This works for both increasing and decreasing metrics.
 */
export const metricProgress = (
  startValue: number | null | undefined,
  currentValue: number | null | undefined,
  targetValue: number | null | undefined,
) => {
  const start = Number(startValue);
  const target = Number(targetValue);
  const suppliedCurrent =
    currentValue === null || currentValue === undefined
      ? Number.NaN
      : Number(currentValue);
  const current = Number.isFinite(suppliedCurrent) ? suppliedCurrent : start;

  if (!Number.isFinite(start) || !Number.isFinite(target)) return 0;
  if (target === start) return current === target ? 100 : 0;

  const percentage = Math.round(((current - start) / (target - start)) * 100);

  return Math.min(100, Math.max(0, percentage));
};

export const computeCompletionMBO = (
  startValue: number,
  targetValue: number,
  currentValue: number,
) => {
  return metricProgress(startValue, currentValue, targetValue);
};

const hasRawMetricRange = (metric: any) =>
  metric?.startValue !== null &&
  metric?.startValue !== undefined &&
  Number.isFinite(Number(metric.startValue)) &&
  metric?.targetValue !== null &&
  metric?.targetValue !== undefined &&
  Number.isFinite(Number(metric.targetValue));

const suppliedProgress = (metric: any) => {
  const supplied = Number(
    String(metric?.percentageProgress ?? '').replace('%', ''),
  );
  return Number.isFinite(supplied)
    ? Math.min(100, Math.max(0, Math.round(supplied)))
    : 0;
};

export const progressForMetric = (metric: any) =>
  hasRawMetricRange(metric)
    ? metricProgress(metric.startValue, metric.currentValue, metric.targetValue)
    : suppliedProgress(metric);
export const computeAverageOfSummedupCompletionPercentOfKeyResults = (
  keyResults: any,
) => {
  const arrayOfCompletion = keyResults.map(progressForMetric);
  const parseCompletionArray = arrayOfCompletion.map((completion: any) =>
    Math.min(100, Math.max(0, completion)),
  );
  if (parseCompletionArray.length === 0) return 0;
  const totalSum = parseCompletionArray.reduce(
    (a: number, b: number) => a + b,
    0,
  );

  const proportion = totalSum / parseCompletionArray.length;
  return Math.round(proportion);
};

export const computeAverageOfSummedupPercentOfKeyResults = (
  keyResults: any,
) => {
  return computeAverageOfSummedupCompletionPercentOfKeyResults(keyResults);
};

export const GoalProgress = (goal: any) => {
  let result = {percentage: 0, value: goal?.currentValue};
  if (goal?.keyResults && goal?.keyResults.length > 0) {
    result.percentage = computeAverageOfSummedupCompletionPercentOfKeyResults(
      goal.keyResults,
    );
    const start = Number(goal?.startValue) || 0;
    const target = Number(goal?.targetValue) || 0;
    const diff = target - start;
    result.value = Number(
      (start + (result.percentage / 100) * diff).toFixed(0),
    );
  } else {
    result.percentage = progressForMetric(goal);
    result.value = goal?.currentValue ?? goal?.startValue ?? 0;
  }
  return result;
};

export const getPercentCompletedKeyResult = (
  startValue: number,
  targetValue: number,
  currentValue: number,
) => {
  return computeCompletionMBO(startValue, targetValue, currentValue);
};

export const getStatusPercentage = (
  statusGoals: number,
  totalGoals: number,
) => {
  const result = statusGoals / totalGoals;

  return !isNaN(result) ? Math.round(result * 100) : 0;
};
export const getStatusRateOfIncreaseBetweenDates = (
  _statusAtFirst: number,
  statusAtLast: number,
) => {
  return statusAtLast;
};

export const getPercentage = (count: number, highestValue: number) => {
  return (count / highestValue) * 100;
};
